import _ from 'underscore';
import { useState, useCallback, useMemo } from 'react';
import { Message } from 'botframework-directlinejs';
import { IAdsCopilotProps } from '../types';
import { getOAuthCardResourceUri } from './useSSO';
import { errorMessageTypes } from './useStaticMessage';
import { BotActivity, StreamingStatusType } from '../botTypes';
import { isTypingMessage, EVENT_NAME_ENGAGE_TROUBLESHOOTER } from '../utils';
import { useAdsCopilotContext } from '../components/ads-copilot-context-provider';

interface IUseInputDisabledParam {
  onSendActivity?: IAdsCopilotProps['onSendActivity'];
  onReceiveActivity?: IAdsCopilotProps['onReceiveActivity'];
  botServiceConfig: IAdsCopilotProps['botServiceConfig'];
}

export const useInputDisabled = ({
  onSendActivity,
  onReceiveActivity,
  botServiceConfig,
}: IUseInputDisabledParam) => {
  const [sendDisabled, setSendDisabled] = useState<boolean | undefined>(false);
  const { onIsSendDisabledChange = _.noop } = useAdsCopilotContext() || {};

  const [inputDisabled, setInputDisabled] = useState<boolean | undefined>(false);

  const blockingErrorMessageTypes = useMemo(() => _.filter(errorMessageTypes, (item) => item !== 'ResponsibleAI' && item !== 'Timeout'), []);

  const handleSendDisabledChange = useCallback((disabled: boolean) => {
    setSendDisabled(disabled);
    onIsSendDisabledChange(disabled);
  }, [onIsSendDisabledChange]);

  const finalOnSendActivity = useCallback<NonNullable<IUseInputDisabledParam['onSendActivity']>>((activity, directLine, isCustomStream) => {
    // 1. disable send button when it is not a custom stream
    // 2. disable send button when it is not a troubleshooting event
    if (!isCustomStream && !(activity.type === 'event' && activity.name === EVENT_NAME_ENGAGE_TROUBLESHOOTER)) {
      handleSendDisabledChange(true);
    }

    if (onSendActivity) {
      return onSendActivity(activity, directLine, isCustomStream);
    }

    return activity;
  }, [onSendActivity]);

  const finalOnReceiveActivity = useCallback<NonNullable<IUseInputDisabledParam['onReceiveActivity']>>((activity, currentActivities, directLine) => {
    const act = activity as Message;
    const messageActivity = activity as BotActivity;

    const isBotMessage = activity.from.name !== botServiceConfig.username;
    const isStreamingOrGenerating = messageActivity.value?.StreamingStatus === StreamingStatusType.InProgress || messageActivity.value === 'AnimationCard';

    if ((act.text === 'StaticMessages' && blockingErrorMessageTypes.indexOf(act.value as any) !== -1)) {
      handleSendDisabledChange(true);
      setInputDisabled(true);
    } else if (isBotMessage && !isTypingMessage(act) && getOAuthCardResourceUri(act) == null) {
      if (isStreamingOrGenerating) {
        // enable input when it is streaming, but disable send button
        handleSendDisabledChange(true);
        setInputDisabled(false);
      } else {
        handleSendDisabledChange(false);
        setInputDisabled(false);
      }
    }

    if (onReceiveActivity) {
      return onReceiveActivity(activity, currentActivities, directLine);
    }

    return currentActivities;
  }, [botServiceConfig.username, onReceiveActivity]);

  return {
    sendDisabled,
    inputDisabled,
    onSendActivity: finalOnSendActivity,
    onReceiveActivity: finalOnReceiveActivity,
  };
};
